import React from "react";
import { Button, Card, Header, Icon, Label, List, Loader, Popup } from "semantic-ui-react";

const WORLD_HERITAGE_CRITERIA_META = {
  i: {
    summary: "Masterpiece of creative genius",
    full: "to represent a masterpiece of human creative genius;",
  },
  ii: {
    summary: "Interchange of human values",
    full: "to exhibit an important interchange of human values, over a span of time or within a cultural area of the world, on developments in architecture or technology, monumental arts, town-planning or landscape design;",
  },
  iii: {
    summary: "Exceptional cultural testimony",
    full: "to bear a unique or at least exceptional testimony to a cultural tradition or to a civilization which is living or which has disappeared;",
  },
  iv: {
    summary: "Historic architectural example",
    full: "to be an outstanding example of a type of building, architectural or technological ensemble or landscape which illustrates (a) significant stage(s) in human history;",
  },
  v: {
    summary: "Traditional settlement and land-use",
    full: "to be an outstanding example of a traditional human settlement, land-use, or sea-use which is representative of a culture (or cultures), or human interaction with the environment especially when it has become vulnerable under the impact of irreversible change;",
  },
  vi: {
    summary: "Ideas, traditions, and beliefs",
    full: "to be directly or tangibly associated with events or living traditions, with ideas, or with beliefs, with artistic and literary works of outstanding universal significance. (The Committee considers that this criterion should preferably be used in conjunction with other criteria);",
  },
  vii: {
    summary: "Exceptional natural beauty",
    full: "to contain superlative natural phenomena or areas of exceptional natural beauty and aesthetic importance;",
  },
  viii: {
    summary: "Major stages of Earth history",
    full: "to be outstanding examples representing major stages of earth's history, including the record of life, significant on-going geological processes in the development of landforms, or significant geomorphic or physiographic features;",
  },
  ix: {
    summary: "Ecological and biological processes",
    full: "to be outstanding examples representing significant on-going ecological and biological processes in the evolution and development of terrestrial, fresh water, coastal and marine ecosystems and communities of plants and animals;",
  },
  x: {
    summary: "Key biodiversity conservation habitats",
    full: "to contain the most important and significant natural habitats for in-situ conservation of biological diversity, including those containing threatened species of outstanding universal value from the point of view of science or conservation.",
  },
};

const parseWorldHeritageCriteria = (criteriaTxt) => {
  if (!criteriaTxt) return [];
  const matches = String(criteriaTxt).toLowerCase().match(/\(([ivx]+)\)/g) || [];
  return matches
    .map((token) => token.replace(/[()]/g, ""))
    .filter((code, index, arr) => arr.indexOf(code) === index);
};

const worldHeritageCategoryLabelColor = (category) => {
  const value = String(category || "").toLowerCase();
  if (value === "cultural") return "red";
  if (value === "natural") return "green";
  if (value === "mixed") return "yellow";
  return "grey";
};

export default function WorldHeritageSiteSidebarPanel({
  open,
  isMobile,
  selectedWorldHeritageSite,
  isLoadingWorldHeritageDetails,
  worldHeritageDetailsError,
  parentWorldHeritageSite,
  isLoadingParentWorldHeritageDetails,
  parentWorldHeritageDetailsError,
  isWorldHeritageComponent,
  worldHeritageCategoryColor,
  onClose,
  showInlineClose = true,
  sticky = true,
}) {
  if (!open || !selectedWorldHeritageSite) return null;
  const componentParentSite = parentWorldHeritageSite || null;
  const renderSiteTopBadges = (site) => {
    if (!site) return null;
    const criteriaCodes = parseWorldHeritageCriteria(site.criteria_txt);
    if (!site.category && criteriaCodes.length === 0) return null;
    return (
      <div
        style={{
          marginTop: "8px",
          marginBottom: "10px",
          display: "flex",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "6px",
        }}
      >
        {site.category && (
          <Label size="small" color={worldHeritageCategoryLabelColor(site.category)} style={{ marginLeft: 0 }}>
            {site.category}
          </Label>
        )}
            {criteriaCodes.map((code) => {
              const meta = WORLD_HERITAGE_CRITERIA_META[code];
              return (
                <Popup
                  key={code}
                  on="click"
                  position="top center"
                  trigger={
                    <Label
                      basic
                      color="blue"
                      size="small"
                      style={{ cursor: "pointer", marginLeft: 0 }}
                      role="button"
                      tabIndex={0}
                    >
                      {meta?.summary || `(${code})`}
                    </Label>
                  }
                  content={meta ? `(${code}) ${meta.full}` : `(${code})`}
                />
              );
            })}
      </div>
    );
  };
  const renderSiteHeaderBlock = (site, { parent = false } = {}) => {
    if (!site) return null;
    return (
      <div style={{ marginBottom: "10px" }}>
        <Header
          as={parent ? "h4" : "h4"}
          style={{
            margin: 0,
            lineHeight: 1.2,
            wordBreak: "break-word",
            fontSize: parent ? (isMobile ? "1.08em" : "1.14em") : undefined,
          }}
        >
          {site.name || "World Heritage Site"}
        </Header>
        {renderSiteTopBadges(site)}
      </div>
    );
  };

  return (
    <Card
      fluid
      style={{
        marginBottom: isMobile ? "8px" : "12px",
        position: sticky ? "sticky" : "relative",
        top: sticky ? 0 : "auto",
        zIndex: 4,
        border: "1px solid rgba(0,0,0,0.1)",
        boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
      }}
    >
      <Card.Content style={{ paddingBottom: "8px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          {isWorldHeritageComponent && (
            <Header
              as="h4"
              style={{ margin: 0, lineHeight: 1.2, flex: 1, minWidth: 0, textAlign: "center" }}
            >
              <Header.Content
                style={{
                  fontSize: isMobile ? "0.95em" : "1em",
                  wordBreak: "break-word",
                  textAlign: "center",
                  display: "block",
                }}
              >
                {selectedWorldHeritageSite?.name || "World Heritage Site Component"}
                <Header.Subheader style={{ textAlign: "center" }}>
                  World Heritage Component
                </Header.Subheader>
              </Header.Content>
            </Header>
          )}
          {showInlineClose && (
            <Button
              icon="close"
              basic
              size="mini"
              onClick={onClose}
              aria-label="Close World Heritage details"
            />
          )}
        </div>

        {isLoadingWorldHeritageDetails && (
          <div style={{ marginTop: "8px", marginBottom: "8px" }}>
            <Loader active inline size="small" /> Loading full details...
          </div>
        )}
        {worldHeritageDetailsError && (
          <div style={{ marginTop: "8px", marginBottom: "8px", color: "#c0392b", fontSize: "0.95em" }}>
            {worldHeritageDetailsError}
          </div>
        )}

        {isWorldHeritageComponent ? (
          <>
            <List size={isMobile ? "small" : undefined}>
              {selectedWorldHeritageSite.parent_site_name && (
                <List.Item>
                  <List.Content>
                    <List.Header>Parent Site</List.Header>
                    <List.Description>{selectedWorldHeritageSite.parent_site_name}</List.Description>
                  </List.Content>
                </List.Item>
              )}
              {(selectedWorldHeritageSite.parent_site_category || selectedWorldHeritageSite.category) && (
                <List.Item>
                  <List.Content>
                    <List.Header>Parent Category</List.Header>
                    <List.Description>
                      {selectedWorldHeritageSite.parent_site_category || selectedWorldHeritageSite.category}
                    </List.Description>
                  </List.Content>
                </List.Item>
              )}
            </List>

            <div
              style={{
                marginTop: "14px",
                paddingTop: "14px",
                borderTop: "2px solid rgba(15, 23, 42, 0.18)",
              }}
            >
              <div
                style={{
                  marginBottom: "10px",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  background: "rgba(15, 23, 42, 0.04)",
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  fontSize: "0.9em",
                  fontWeight: 700,
                  color: "#334155",
                  letterSpacing: "0.02em",
                }}
              >
                Parent Site
              </div>

              {isLoadingParentWorldHeritageDetails && (
                <div style={{ marginBottom: "8px" }}>
                  <Loader active inline size="small" /> Loading parent site details...
                </div>
              )}

              {parentWorldHeritageDetailsError && (
                <div style={{ marginBottom: "8px", color: "#c0392b", fontSize: "0.95em" }}>
                  {parentWorldHeritageDetailsError}
                </div>
              )}

              {componentParentSite && (
                <>
                  {renderSiteHeaderBlock(componentParentSite, { parent: true })}
                  <List size={isMobile ? "small" : undefined}>
                    {componentParentSite.date_inscribed && (
                      <List.Item>
                        <List.Content>
                          <List.Description style={{ lineHeight: 1.4 }}>
                            <strong>Inscribed:</strong> {componentParentSite.date_inscribed}
                          </List.Description>
                        </List.Content>
                      </List.Item>
                    )}
                    {componentParentSite.states_names_text && (
                      <List.Item>
                        <List.Content>
                          <List.Description style={{ lineHeight: 1.4 }}>
                            <strong>State(s):</strong> {componentParentSite.states_names_text}
                          </List.Description>
                        </List.Content>
                      </List.Item>
                    )}
                  </List>

                  {(componentParentSite.short_description_en ||
                    componentParentSite.source_properties?.short_description_en) && (
                    <div style={{ marginTop: "8px" }}>
                      <Header as="h5" style={{ marginBottom: "8px" }}>
                        Description
                      </Header>
                      <p style={{ margin: 0, lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                        {componentParentSite.short_description_en ||
                          componentParentSite.source_properties?.short_description_en}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <>
            {renderSiteHeaderBlock(selectedWorldHeritageSite)}
            <List size={isMobile ? "small" : undefined}>
              {selectedWorldHeritageSite.date_inscribed && (
                <List.Item>
                  <List.Content>
                    <List.Description style={{ lineHeight: 1.4 }}>
                      <strong>Inscribed:</strong> {selectedWorldHeritageSite.date_inscribed}
                    </List.Description>
                  </List.Content>
                </List.Item>
              )}
              {selectedWorldHeritageSite.states_names_text && (
                <List.Item>
                  <List.Content>
                    <List.Description style={{ lineHeight: 1.4 }}>
                      <strong>State(s):</strong> {selectedWorldHeritageSite.states_names_text}
                    </List.Description>
                  </List.Content>
                </List.Item>
              )}
              {selectedWorldHeritageSite.danger !== undefined && (
                <List.Item>
                  <List.Content>
                    <List.Description>
                      <strong>Danger List:</strong>{" "}
                      {String(selectedWorldHeritageSite.danger) === "true" ||
                      Number(selectedWorldHeritageSite.danger) === 1
                        ? "Yes"
                        : "No"}
                    </List.Description>
                  </List.Content>
                </List.Item>
              )}
            </List>
            {(selectedWorldHeritageSite.short_description_en ||
              selectedWorldHeritageSite.source_properties?.short_description_en) && (
              <div style={{ marginTop: "12px" }}>
                <Header as="h5" style={{ marginBottom: "8px" }}>
                  Description
                </Header>
                <p style={{ margin: 0, lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                  {selectedWorldHeritageSite.short_description_en ||
                    selectedWorldHeritageSite.source_properties?.short_description_en}
                </p>
              </div>
            )}
          </>
        )}
      </Card.Content>
    </Card>
  );
}
